import { useTranslation } from 'react-i18next';

import { TrendIndicator } from '@shared/ui';
import { resolveAnalyticsTrend } from '../lib/trend';

export interface AnalyticsPercentTrendProps {
  appearance?: 'inline' | 'badge';
  className?: string;
  context?: string;
  formatValue?: (value: number) => string;
  locale?: string;
  positiveIsGood?: boolean;
  value: number | null | undefined;
  wrap?: 'nowrap' | 'wrap';
}

export function AnalyticsPercentTrend({
  appearance = 'badge',
  className,
  context,
  formatValue,
  locale,
  positiveIsGood = true,
  value,
  wrap = 'nowrap',
}: AnalyticsPercentTrendProps) {
  const { t } = useTranslation();

  if (value === null || value === undefined || !Number.isFinite(value)) {
    return <span className={className}>{t('analytics.weekly.notAvailable', 'Unavailable')}</span>;
  }

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const formattedMagnitude = formatter.format(Math.abs(value));
  const formattedValue = formatValue
    ? formatValue(value)
    : `${value > 0 ? '+' : value < 0 ? '-' : ''}${formattedMagnitude}%`;
  const trend = resolveAnalyticsTrend(value, positiveIsGood);
  const accessibleContext = context || t('common.trend.changeContext', 'change');
  const sentimentLabel = t(`common.trend.sentiment.${trend.sentiment}`, trend.sentiment);
  const directionLabel = t(`common.trend.direction.${trend.direction}`, trend.direction);
  const accessibleLabel = `${directionLabel}: ${formattedValue} ${accessibleContext}; ${sentimentLabel}`;

  return (
    <TrendIndicator
      accessibleLabel={accessibleLabel}
      appearance={appearance}
      className={className}
      context={context || undefined}
      direction={trend.direction}
      sentiment={trend.sentiment}
      sentimentLabel={sentimentLabel}
      value={formattedValue}
      wrap={wrap}
    />
  );
}
